/**
 * Representative IANA timezone per ISO country code.
 *
 * MIRRORS the backend `flowdesk-api/src/utils/geo.js` COUNTRY_TIMEZONE map —
 * keep the two in sync. Used to SUGGEST a company timezone when the admin
 * changes the Company country in My Preferences (Region & formats); the admin still reviews and
 * clicks "Save timezone", so nothing is overwritten silently. For MULTI-ZONE
 * countries (US, RU, AU, …) this is one representative national zone, not exact.
 * Unmapped countries return null (no suggestion).
 */
const COUNTRY_TIMEZONE = {
  IN: 'Asia/Kolkata', GB: 'Europe/London', IE: 'Europe/Dublin',
  DE: 'Europe/Berlin', FR: 'Europe/Paris', ES: 'Europe/Madrid',
  IT: 'Europe/Rome', NL: 'Europe/Amsterdam', BE: 'Europe/Brussels',
  CH: 'Europe/Zurich', SE: 'Europe/Stockholm', PL: 'Europe/Warsaw',
  PT: 'Europe/Lisbon', RU: 'Europe/Moscow', TR: 'Europe/Istanbul',
  SG: 'Asia/Singapore', MY: 'Asia/Kuala_Lumpur', ID: 'Asia/Jakarta',
  PH: 'Asia/Manila', TH: 'Asia/Bangkok', VN: 'Asia/Ho_Chi_Minh',
  HK: 'Asia/Hong_Kong', CN: 'Asia/Shanghai', TW: 'Asia/Taipei',
  JP: 'Asia/Tokyo', KR: 'Asia/Seoul', PK: 'Asia/Karachi',
  BD: 'Asia/Dhaka', LK: 'Asia/Colombo', NP: 'Asia/Kathmandu',
  AE: 'Asia/Dubai', SA: 'Asia/Riyadh', QA: 'Asia/Qatar',
  KW: 'Asia/Kuwait', BH: 'Asia/Bahrain', OM: 'Asia/Muscat',
  IL: 'Asia/Jerusalem', US: 'America/New_York', CA: 'America/Toronto',
  BR: 'America/Sao_Paulo', MX: 'America/Mexico_City', AR: 'America/Argentina/Buenos_Aires',
  CO: 'America/Bogota', AU: 'Australia/Sydney', NZ: 'Pacific/Auckland',
  ZA: 'Africa/Johannesburg', NG: 'Africa/Lagos', KE: 'Africa/Nairobi',
  EG: 'Africa/Cairo', MA: 'Africa/Casablanca',
}

export function timezoneForCountry(code) {
  if (!code || typeof code !== 'string') return null
  return COUNTRY_TIMEZONE[code.toUpperCase()] ?? null
}

/**
 * IANA timezone → ISO country code. The inverse direction, used to SUGGEST a
 * company country when the admin changes the Company timezone (they still Save
 * it). Richer than reversing COUNTRY_TIMEZONE: it maps the multiple zones of
 * big countries back to one country (every US zone → US, etc.). Also the single
 * source of truth for the phone field's browser-timezone → country guess
 * (`PhoneInputField`). Unmapped zones return null (no suggestion).
 */
export const TIMEZONE_COUNTRY = {
  'Asia/Kolkata': 'IN', 'Asia/Calcutta': 'IN',
  'Asia/Karachi': 'PK', 'Asia/Dhaka': 'BD', 'Asia/Colombo': 'LK',
  'Asia/Kathmandu': 'NP', 'Asia/Thimphu': 'BT',
  'Asia/Dubai': 'AE', 'Asia/Riyadh': 'SA', 'Asia/Qatar': 'QA',
  'Asia/Kuwait': 'KW', 'Asia/Bahrain': 'BH', 'Asia/Muscat': 'OM',
  'Asia/Amman': 'JO', 'Asia/Beirut': 'LB', 'Asia/Jerusalem': 'IL',
  'Asia/Singapore': 'SG', 'Asia/Kuala_Lumpur': 'MY', 'Asia/Jakarta': 'ID',
  'Asia/Manila': 'PH', 'Asia/Bangkok': 'TH', 'Asia/Ho_Chi_Minh': 'VN',
  'Asia/Hong_Kong': 'HK', 'Asia/Shanghai': 'CN', 'Asia/Taipei': 'TW',
  'Asia/Tokyo': 'JP', 'Asia/Seoul': 'KR',
  'Europe/London': 'GB', 'Europe/Dublin': 'IE', 'Europe/Paris': 'FR',
  'Europe/Berlin': 'DE', 'Europe/Madrid': 'ES', 'Europe/Rome': 'IT',
  'Europe/Amsterdam': 'NL', 'Europe/Brussels': 'BE', 'Europe/Zurich': 'CH',
  'Europe/Stockholm': 'SE', 'Europe/Warsaw': 'PL', 'Europe/Lisbon': 'PT',
  'Europe/Moscow': 'RU', 'Europe/Istanbul': 'TR',
  'America/New_York': 'US', 'America/Chicago': 'US', 'America/Denver': 'US',
  'America/Los_Angeles': 'US', 'America/Phoenix': 'US', 'America/Anchorage': 'US',
  'America/Toronto': 'CA', 'America/Vancouver': 'CA', 'America/Edmonton': 'CA',
  'America/Sao_Paulo': 'BR', 'America/Mexico_City': 'MX',
  'America/Argentina/Buenos_Aires': 'AR', 'America/Bogota': 'CO',
  'Australia/Sydney': 'AU', 'Australia/Melbourne': 'AU',
  'Australia/Brisbane': 'AU', 'Australia/Perth': 'AU',
  'Pacific/Auckland': 'NZ',
  'Africa/Johannesburg': 'ZA', 'Africa/Lagos': 'NG', 'Africa/Nairobi': 'KE',
  'Africa/Cairo': 'EG', 'Africa/Casablanca': 'MA',
}

export function countryForTimezone(tz) {
  if (!tz || typeof tz !== 'string') return null
  return TIMEZONE_COUNTRY[tz] ?? null
}
